import { LOCATION_LABEL, type MethodId } from "@h2lca/core";
import { methodById, useMaster } from "@/store/master";
import { GlossaryTip } from "./GlossaryTip";
import { DENOMINATOR_LABEL } from "./helpers";
import { Block, Section } from "./Section";

/** 시작 화면에서 선택한 산정 방식의 적용 기준. 작업 단계와 별도로 펼쳐 본다. */
export function MethodCriteria({ method }: { method: MethodId }) {
  const data = useMaster((s) => s.data);
  const m = methodById(data, method);

  return (
    <Section
      title={
        <>
          적용 기준 <GlossaryTip k="cert" />
        </>
      }
      desc="선택한 산정 방식에 적용되는 표준과 계산조건입니다."
    >
      <Block title={m.label}>
        <p className="text-caption leading-relaxed text-ink-soft">{m.description}</p>
        <dl className="ws-kv">
          <dt>적용 표준과 근거</dt>
          <dd className="font-sans">
            <ul className="list-disc pl-section">
              {m.standards.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </dd>
          <dt>
            GWP 기준 <GlossaryTip k="gwp" />
          </dt>
          <dd className="font-sans">
            {m.gwp.basis} <span className="font-mono text-ink-muted">(CO2 {m.gwp.co2}, CH4 {m.gwp.ch4}, N2O {m.gwp.n2o})</span>
          </dd>
          <dt>
            시스템 경계 <GlossaryTip k={m.id === "cert" ? "well_to_gate" : "system_boundary"} />
          </dt>
          <dd className="font-sans">{m.boundary.description}</dd>
          <dt>기능단위당 분모</dt>
          <dd className="font-sans">{DENOMINATOR_LABEL[m.denominator]}</dd>
        </dl>
      </Block>

      {m.id === "cert" && (
        <Block title="인증제 모의산정에서 잠기는 조건">
          <dl className="ws-kv">
            <dt>기능단위</dt>
            <dd>수소 {m.functionalUnit.h2Kg} kg</dd>
            <dt>순도 조건</dt>
            <dd>{m.functionalUnit.purityMinPct !== undefined ? `${m.functionalUnit.purityMinPct}% 이상` : "없음"}</dd>
            <dt>산정범위</dt>
            <dd className="font-sans">Well-to-Gate, {m.boundary.includeLocations.map((l) => LOCATION_LABEL[l]).join(" 및 ")}</dd>
            <dt>분모</dt>
            <dd className="font-sans">{DENOMINATOR_LABEL[m.denominator]} (출하 이후 손실 미반영)</dd>
            {m.grades && (
              <>
                <dt>등급(교육용 참고)</dt>
                <dd className="font-sans">
                  {m.grades.map((g) => `${g.grade}등급 이하 ${g.maxKgCO2eq}`).join(", ")} kgCO2eq/kgH2
                </dd>
              </>
            )}
          </dl>
          <p className="text-footnote text-ink-subtle">출하 이후(하류) 설비는 화면에 남기되 회색으로 표시하고 계산에서 제외합니다.</p>
        </Block>
      )}

      <ul className="list-disc pl-section text-footnote leading-relaxed text-ink-subtle">
        {m.notices.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
    </Section>
  );
}
